import { Link } from '@tanstack/react-router';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';

import { cn } from '#/utils/cn';

import type { IconId } from './icons';
import { Icon } from './icons';

export type ButtonVariant =
  | 'primary'
  | 'outline-primary'
  | 'secondary'
  | 'outline-secondary'
  | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonBaseProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  prefixIcon?: IconId;
  suffixIcon?: IconId;
};

type AsButton = ButtonBaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & { href?: never; to?: never };

type AsLink = ButtonBaseProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; to?: never };

type AsRouterLink = ButtonBaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'type'> & {
    href?: never;
    to: string;
  };

export type ButtonProps = AsButton | AsLink | AsRouterLink;

const iconSize: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18 };

// NOTE: Button styles are separated for easier maintenance and parsing, see src/styles/buttons.css

export function Button({
  variant = 'primary',
  size = 'md',
  prefixIcon,
  suffixIcon,
  children,
  className,
  ...props
}: ButtonProps) {
  const classes = cn(
    'mdc-btn',
    `mdc-btn--${variant}`,
    `mdc-btn--${size}`,
    className,
  );

  const content = (
    <>
      {prefixIcon && (
        <Icon id={prefixIcon} size={iconSize[size]} aria-hidden="true" />
      )}
      {children}
      {suffixIcon && (
        <Icon id={suffixIcon} size={iconSize[size]} aria-hidden="true" />
      )}
    </>
  );

  if ('href' in props && props.href != null) {
    return (
      <a className={classes} {...props}>
        {content}
      </a>
    );
  }

  if ('to' in props) {
    const { to, ...linkProps } = props as AsRouterLink;

    return (
      <Link className={classes} to={to} {...linkProps}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className={classes} {...props}>
      {content}
    </button>
  );
}
